
'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, ShieldCheck, Crown, Check, X, 
  HelpCircle, ChevronDown, Plus, 
  FilePlus, Rocket, RefreshCw,
  Send, ArrowUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from '@/navigation';

export default function PricingPage() {
  const t = useTranslations('pricing');
  
  const scrollToTop = () => {
    const plansSection = document.getElementById('plans');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <main className="min-h-screen bg-white dark:bg-black font-sans">
      <Navbar />

      {/* HERO SECTION */}
      <section className="py-24 md:py-40 text-center">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 border border-gray-200 rounded-full mb-8 dark:border-gray-800"
          >
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-black dark:text-white">
              {t('title')}
            </span>
          </motion.div>
          
          <h1 className="text-4xl md:text-7xl font-canva font-bold tracking-tight mb-8 dark:text-white leading-[1.1]">
            {t('subtitle')}
          </h1>
          
          <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-12 dark:text-gray-400">
            {t('desc')}
          </p>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-amber-700 text-sm font-medium dark:bg-amber-900/20 dark:border-amber-900/30 dark:text-amber-400">
            {t('warning_pill')}
          </div>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section id="plans" className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Landing */}
            <PriceCard 
              plan={t.raw('plans.landing')} 
              icon={Zap} 
              btnLabel={t('cta_btn')}
            />
            {/* Business - Highlighted */}
            <PriceCard 
              plan={t.raw('plans.multi')} 
              icon={ShieldCheck} 
              highlighted 
              btnLabel={t('cta_btn')}
            />
            {/* Premium */}
            <PriceCard 
              plan={t.raw('plans.premium')} 
              icon={Crown} 
              btnLabel={t('cta_btn')}
            />
          </div>
        </div>
      </section>

      {/* WHAT'S NOT INCLUDED */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-amber-50 border border-amber-200 rounded-[2rem] p-8 md:p-12 dark:bg-zinc-900/50 dark:border-amber-900/20">
            <h2 className="text-3xl font-bold mb-4 dark:text-white">{t('warning_title')}</h2>
            <p className="text-gray-600 mb-12 dark:text-gray-400">{t('warning_desc')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
              {t.raw('not_included').map((item: string, i: number) => (
                <div key={i} className="flex items-center gap-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  <X size={18} className="text-red-500 shrink-0" /> {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-24 bg-gray-50 dark:bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-display italic mb-6 dark:text-white">{t('addons.title')}</h2>
            <p className="text-gray-500 dark:text-gray-400">{t('addons.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {t.raw('addons.items').map((addon: any, i: number) => {
              const icons = [FilePlus, Rocket, RefreshCw];
              const Icon = icons[i] || FilePlus;
              return (
                <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-xl transition-all dark:bg-zinc-900 dark:border-zinc-800">
                  <div className="w-10 h-10 bg-black text-white flex items-center justify-center rounded-xl mb-6 dark:bg-white dark:text-black">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-bold text-lg mb-1 dark:text-white">{addon.title}</h3>
                  <div className="text-indigo-600 font-mono text-sm font-bold mb-4">{addon.price}</div>
                  <p className="text-xs text-gray-500 leading-relaxed dark:text-gray-400">{addon.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-display italic mb-20 text-center dark:text-white">{t('faq.title')}</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {t.raw('faq.items').map((item: any, i: number) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-gray-100 dark:border-zinc-800 py-4">
                  <AccordionTrigger className="text-left text-lg font-bold hover:no-underline hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-500 leading-relaxed pt-4 text-base dark:text-gray-400">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-32 bg-black text-white text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-6xl font-display italic mb-8">Qaysi paket sizga mos?</h2>
          <p className="text-xl text-gray-400 max-w-xl mx-auto mb-12">Bepul konsultatsiya oling — loyihangizni muhokama qilamiz</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-full h-16 px-12 bg-white text-black hover:bg-gray-100 text-lg group">
              <a href="https://t.me/hvnimperiyasi" target="_blank" rel="noopener noreferrer">
                <Send size={20} className="mr-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                Telegram orqali yozish
              </a>
            </Button>
            <Button variant="outline" size="lg" className="rounded-full h-16 px-12 border-white/20 hover:bg-white/5 text-lg" onClick={scrollToTop}>
              <ArrowUp size={20} className="mr-2" /> Narxlarni solishtirish
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function PriceCard({ plan, highlighted, icon: Icon, btnLabel }: { plan: any, highlighted?: boolean, icon: any, btnLabel: string }) {
  return (
    <div className={`rounded-3xl p-10 flex flex-col transition-all duration-500 border relative ${highlighted ? 'bg-black text-white shadow-2xl scale-105 z-10 border-transparent dark:bg-white dark:text-black' : 'bg-white border-gray-100 hover:border-black dark:bg-zinc-900 dark:border-zinc-800 dark:hover:border-white'}`}>
      {highlighted && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
          {plan.badge}
        </div>
      )}
      
      <div className="flex justify-between items-start mb-8">
        <div>
          <h4 className="font-mono text-xs font-bold tracking-[0.2em] opacity-60 uppercase mb-2">{plan.name}</h4>
          <p className="text-xs italic opacity-60">{plan.tagline}</p>
        </div>
        <div className={`p-3 rounded-2xl ${highlighted ? 'bg-white/10 dark:bg-black/5' : 'bg-gray-50 dark:bg-zinc-800'}`}>
          <Icon size={24} className={highlighted ? 'text-white dark:text-black' : 'text-gray-900 dark:text-white'} />
        </div>
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-6xl font-instrument italic font-bold tracking-tighter">{plan.price}</span>
        <span className="text-sm opacity-60">/ loyiha</span>
      </div>
      <p className="text-sm font-medium mb-10 text-indigo-500">{plan.duration}</p>
      
      <div className={`h-px mb-10 ${highlighted ? 'bg-white/10 dark:bg-black/10' : 'bg-gray-100 dark:bg-zinc-800'}`} />
      
      <ul className="space-y-4 mb-12 flex-1">
        {plan.features?.map((item: any, i: number) => (
          <li key={i} className={`flex items-start gap-3 text-sm ${!item.included ? 'opacity-30' : ''}`}>
            {item.included ? (
              <Check size={18} className="text-green-500 shrink-0" />
            ) : (
              <X size={18} className="text-red-500 shrink-0" />
            )}
            <span className="leading-tight">{item.text}</span>
          </li>
        ))}
      </ul>

      <Button asChild className={`w-full rounded-full h-14 font-bold transition-all hover:scale-[1.02] ${highlighted ? 'bg-white text-black hover:bg-gray-100 dark:bg-black dark:text-white' : 'bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black'}`}>
        <Link href="/#contact">
          {btnLabel}
        </Link>
      </Button>
    </div>
  );
}
